import { useState } from "react";
import {
  Box,
  Text,
  Flex,
  Card,
  Button,
  AlertDialog,
  Callout,
} from "@radix-ui/themes";
import {
  CheckCircledIcon,
  TrashIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";
import { clearImportData, type ImportMeta } from "../../../../lib/import-db";

interface InfoTabProps {
  importRecord: ImportMeta;
  onCleared: () => void;
}

export function InfoTab({ importRecord, onCleared }: InfoTabProps) {
  const { t } = useTranslation();
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClearImport = async () => {
    setIsClearing(true);
    setError(null);

    try {
      // Clear all import data from IndexedDB
      await clearImportData();
      onCleared();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setIsClearing(false);
    }
  };

  return (
    <Box>
      <Card style={{ maxWidth: 600 }}>
        <Flex direction="column" gap="4" p="4">
          <Flex align="center" gap="2">
            <CheckCircledIcon color="var(--green-9)" width="20" height="20" />
            <Text size="3" weight="medium">
              {t("import.uploadSuccess")}
            </Text>
          </Flex>

          {importRecord.notes && (
            <Callout.Root color="amber">
              <Callout.Icon>
                <InfoCircledIcon />
              </Callout.Icon>
              <Callout.Text>
                <Text as="span" weight="medium">
                  {t("import.importNotes")}
                </Text>
                <Text
                  as="span"
                  size="1"
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    display: "block",
                    marginTop: "8px",
                  }}
                >
                  {importRecord.notes}
                </Text>
              </Callout.Text>
            </Callout.Root>
          )}

          {importRecord.uploadStarted && (
            <Text size="2" color="gray">
              {t("import.started")}{" "}
              {new Date(importRecord.uploadStarted).toLocaleString()}
            </Text>
          )}

          {importRecord.uploadCompleted && (
            <Text size="2" color="gray">
              {t("import.completed")}{" "}
              {new Date(importRecord.uploadCompleted).toLocaleString()}
            </Text>
          )}

          {error && (
            <Text size="2" color="red">
              {error}
            </Text>
          )}

          <AlertDialog.Root>
            <AlertDialog.Trigger>
              <Button variant="soft" color="red" disabled={isClearing}>
                <TrashIcon />
                {isClearing ? t("import.clearing") : t("import.clearImport")}
              </Button>
            </AlertDialog.Trigger>
            <AlertDialog.Content>
              <AlertDialog.Title>{t("import.clearTitle")}</AlertDialog.Title>
              <AlertDialog.Description>
                {t("import.clearDescription")}
              </AlertDialog.Description>
              <Flex gap="3" mt="4" justify="end">
                <AlertDialog.Cancel>
                  <Button variant="soft" color="gray">
                    {t("common.cancel")}
                  </Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action>
                  <Button
                    variant="solid"
                    color="red"
                    onClick={handleClearImport}
                  >
                    {t("import.clearAll")}
                  </Button>
                </AlertDialog.Action>
              </Flex>
            </AlertDialog.Content>
          </AlertDialog.Root>
        </Flex>
      </Card>
    </Box>
  );
}
